import { generateClient } from "aws-amplify/api";
import { createBox, updateBox, deleteBox, updateBoxCost, createBoxCost } from "graphql/customs/mutations";
import { boxesByDate, boxesByPoBox, getBox, listBoxes } from "graphql/queries";
import { cleanUpData, getDataFromResponse, getNotDeletedItems } from "utils/Api";
import { PACKAGE_NOTIFICATIONS, REFERAL_AMOUNT_BENEFITS, SEQUENCETYPES } from "utils/Constants";
import { sendEmailNotification } from "utils/EmailNotifications";
import { poBoxLabelGenerator, sequenceGenerator } from "utils/Form";
import { toNumberFormat } from "utils/Utils";
import { deleteAsset, saveAsset } from "./AssetApi";
import { getTypeCounterByName, increaseTypeCounter } from "./TypeCounterApi";
import { getPoBoxReferales, savePoBoxReferal } from "./PoBoxReferalApi";
import { savePoBoxReferalPoint } from "./PoBoxReferalPointAPI";
import { getPoBox } from "./POBoxApi";

const API = generateClient();

const generateReferalPromiseIfNeeded = async (packageData) => {
    const { isDelivered = false, poBoxBoxesId: poBoxId } = packageData;
    if (isDelivered && poBoxId) {
        const referals = await getPoBoxReferales({ poBoxReferalReferToPoBoxId: { eq: poBoxId }, referalCompleted: { eq: false } })
        if (referals.length) {
            const poBox = await getPoBox(poBoxId);
            const referalData = referals[0];
            await savePoBoxReferal({
                ...referalData,
                referalCompleted: true
            });
            const referalPointEntry = {
                poBoxReferalPointReferalPoBoxId: referalData.poBoxReferalReferFromPoBoxId,
                amount: REFERAL_AMOUNT_BENEFITS,
                description: `Felicidades tu amigo ${poBox.name} => ${poBoxLabelGenerator(poBox)} ha completa su primer envio con Unboxing, Se asignaron $${REFERAL_AMOUNT_BENEFITS} en créditos por la referencia de tu amigo `
            };
            await savePoBoxReferalPoint(referalPointEntry);
        }
    }
}

export const savePackage = async (data, files) => {
    const {
        notificationNeeded = false,
        notificationType = PACKAGE_NOTIFICATIONS.PACKAGE_RECEIVED,
        notificacionData = {} } = data;
    const operationName = (data && data.id) ? "update" : "create";
    const operation = operationName === "update" ? updateBox : createBox;
    let cleanData = cleanUpData(data);

    const referalPromise = () => generateReferalPromiseIfNeeded(data);

    cleanData["type"] = "Box";
    delete cleanData["cargoMovement"];
    delete cleanData["poBox"];
    delete cleanData["boxAssets"];
    delete cleanData["boxes"];
    delete cleanData["boxCost"];
    delete cleanData["deliveryRoute"];
    delete cleanData["notificationNeeded"];
    delete cleanData["notificationType"];
    delete cleanData["notificacionData"];

    cleanData["tracking_num"] = (cleanData["tracking_num"] || "").toUpperCase().replace(/ /g, '');

    let counter;
    if (operationName === "create") {
        counter = await getTypeCounterByName(SEQUENCETYPES.BOX);
        const sequenceNumber = sequenceGenerator(SEQUENCETYPES.BOX, counter);
        cleanData = {
            ...cleanData,
            secuencia: sequenceNumber
        };
    }

    const packageResponse = await API.graphql({
        query: operation,
        variables: {
            "input": cleanData
        }
    });
    if (operationName === "create") {
        await increaseTypeCounter(counter)
    }
    const savedData = getDataFromResponse(packageResponse, operationName === "update" ? "updateBox" : "createBox");
    if (files && files.length > 0) {
        const saveFilesPromises = files.map((f) => {
            const assetData = {
                ...f,
                boxBoxAssetsId: savedData.id
            };
            return saveAsset(assetData, `packages/${savedData.id}/assets/`);
        });
        await Promise.all(saveFilesPromises);
    }
    if (notificationNeeded && notificationType) {
        const notificacionFullData = {
            ...notificacionData,
            ...savedData
        }
        sendEmailNotification(notificationType, notificacionFullData, data.poBox);
    }
    await referalPromise();
    return savedData;
};

export const savePackageCost = async (data) => {
    const operationName = (data && data.id) ? "update" : "create";
    const operation = operationName === "update" ? updateBoxCost : createBoxCost;
    let cleanData = cleanUpData(data);

    cleanData["type"] = "BoxCost";

    const packageCostResponse = await API.graphql({
        query: operation,
        variables: {
            "input": cleanData
        }
    });

    const savedData = getDataFromResponse(packageCostResponse, operationName === "create" ? "createBoxCost" : "updateBoxCost");
    const packageData = await getPackage(data.boxCostBoxId);

    const newPackageData = {
        ...packageData,
        boxBoxCostId: savedData.id,
    };

    const costTable = getCostTableValues(data.data);
    const boxSavedData = await savePackage(newPackageData);
    const notificacionFullData = {
        ...boxSavedData,
        ...costTable,
        subtotal: `${toNumberFormat(data.subtotal)}$ USD`,
        iva: `${toNumberFormat(data.iva)}$ USD`,
        total: `${toNumberFormat(data.total)}$ USD`,
    }
    sendEmailNotification(PACKAGE_NOTIFICATIONS.PACKAGE_COST, notificacionFullData, boxSavedData.poBox);
    return savedData;
};

const getCostTableValues = (costTableJson) => {
    const costTableEntries = JSON.parse(costTableJson);
    return costTableEntries.reduce((a, entry) => {
        return {
            ...a,
            [entry.key]: `${toNumberFormat(entry.price)}$ USD`,
        }
    }, {});
}

export const getPackages = async ({
    sortDirection = "DESC",
    limit = 50,
    nextToken = null,
    filter = null,
} = {}) => {
    const response = await API.graphql({
        query: boxesByDate,
        variables: {
            "filter": filter,
            "type": "Box",
            sortDirection,
            limit,
            nextToken,
        }
    })

    const result = response?.data?.boxesByDate || {};
    const items = getNotDeletedItems(response, "boxesByDate");

    return {
        items: items || [],
        nextToken: result.nextToken || null,
    };
}

export const getBoxesByPoBox = async ({
    poBoxId,
    limit = 50,
    nextToken = null,
    filter = null,
    sortDirection = "DESC",
} = {}) => {
    const resp = await API.graphql({
        query: boxesByPoBox,
        variables: {
            poBoxBoxesId: poBoxId,
            limit,
            nextToken,
            filter,
            sortDirection
        },
    });

    const data = resp?.data?.boxesByPoBox;
    return {
        items: data?.items || [],
        nextToken: data?.nextToken || null,
    };
};

export const getUserPackages = async (filter = {}) => {
    const data = await API.graphql({
        query: boxesByDate,
        variables: {
            "filter": filter,
            "type": "Box",
            "sortDirection": "DESC"
        }
    })
    return getNotDeletedItems(data, "boxesByDate");
}

export const getPackage = async (packageId) => {
    let packageObject = await API.graphql({
        query: getBox,
        variables: {
            id: packageId
        }
    });
    return getDataFromResponse(packageObject, "getBox");
}

export const deletePackage = async (packageData) => {
    const deletedAsset = await API.graphql({
        query: deleteBox,
        variables: {
            input: {
                "id": packageData.id,
            }
        }
    });
    const boxAssets = (packageData.boxAssets && packageData.boxAssets.items) || [];
    const deleteFilesPromises = boxAssets.map((a) => {
        return deleteAsset(a, a.path);
    });
    await Promise.all(deleteFilesPromises);
    return getDataFromResponse(deletedAsset, "deleteBox");
}

export const getPackageTrackingNumber = async (trackingNumber = "") => {
    const data = await API.graphql({
        query: listBoxes,
        variables: {
            "filter": {
                "tracking_num": {
                    "eq": trackingNumber
                }
            }
        }
    })
    const box = getNotDeletedItems(data, "listBoxes");
    if (box.length !== 0) {
        return box[0];
    }
    return null;
}